import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { toast } from '../common/Toast/toast';
import { canvasToBlob, composeImage, drawStroke } from './captureScreen';
import { formatDevMessages, getDevMessages } from '../../../utils/devMessages';
import '../Trainer/TrainerDashboard.css';
import './screen-capture.css';

/* Marker colours are what the drawing IS, not decoration, so they are
   fixed rather than taken from the palette. The highlighter is wide and
   see-through so it marks text without hiding it. */
const MARKERS = [
  { key: 'red', label: 'Red', color: '#E5383B', alpha: 1, scale: 1 },
  { key: 'yellow', label: 'Highlight', color: '#FFD60A', alpha: 0.4, scale: 3.5 },
  { key: 'blue', label: 'Blue', color: '#1E6FD9', alpha: 1, scale: 1 },
];

// How long the Scan App overlay sweeps the screen -- one pass of the line.
const SCAN_MS = 1600;

const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

/**
 * Mark up a screenshot, write a note, then copy or download it.
 *
 * A view popup in the attendance popup's frame (view_popup_rules), with
 * the actions in the footer beside Close. The note comes first, above the
 * image, so it is in view the moment the popup opens; it is required, and
 * it is printed under the image so a pasted screenshot always explains
 * itself. Portalled to <body>: it is opened from the topbar, which must
 * not become its containing block.
 */
const ScreenCaptureModal = ({ shot, taker, onClose }) => {
  const canvasRef = useRef(null);
  const committedRef = useRef(null);
  const drawingRef = useRef(null);
  const [strokes, setStrokes] = useState([]);
  const [marker, setMarker] = useState(MARKERS[0].key);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  // The last "Scan App": console errors and failed API calls since the app
  // loaded (utils/devMessages.js). null until the button is pressed.
  const [scan, setScan] = useState(null);

  const hasNote = note.trim().length > 0;

  // Who took it, from the signed-in account -- printed under the note.
  const takenBy = [taker?.name, taker?.email].map((v) => String(v || '').trim()).filter(Boolean).join(' · ');
  const byline = takenBy ? `Taken by ${takenBy}` : '';

  // Pen width follows the image, so a mark reads the same on any screen.
  const penWidth = Math.max(4, Math.round(shot.width / 320));

  // The committed layer is the screenshot plus every finished stroke. A
  // stroke in progress is drawn over it on each move, so a see-through
  // highlighter never darkens where it crosses itself.
  useEffect(() => {
    const layer = committedRef.current || document.createElement('canvas');
    layer.width = shot.width;
    layer.height = shot.height;
    const ctx = layer.getContext('2d');
    ctx.drawImage(shot, 0, 0);
    strokes.forEach((stroke) => drawStroke(ctx, stroke));
    committedRef.current = layer;
    canvasRef.current?.getContext('2d').drawImage(layer, 0, 0);
  }, [shot, strokes]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const pointOf = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const paint = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.drawImage(committedRef.current, 0, 0);
    if (drawingRef.current) drawStroke(ctx, drawingRef.current);
  };

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pen = MARKERS.find((m) => m.key === marker) || MARKERS[0];
    drawingRef.current = {
      color: pen.color,
      alpha: pen.alpha,
      width: penWidth * pen.scale,
      points: [pointOf(e)],
    };
    paint();
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current.points.push(pointOf(e));
    paint();
  };

  const finishStroke = () => {
    const stroke = drawingRef.current;
    if (!stroke) return;
    drawingRef.current = null;
    setStrokes((prev) => [...prev, stroke]);
  };

  const copyImage = async () => {
    if (!hasNote) return;
    if (!navigator.clipboard?.write || typeof window.ClipboardItem === 'undefined') {
      toast.error('This browser cannot copy images. Use Download instead.');
      return;
    }
    setBusy('copy');
    try {
      // The blob is handed over as a promise: Safari only allows a clipboard
      // write that starts inside the click itself.
      await navigator.clipboard.write([
        new window.ClipboardItem({ 'image/png': canvasToBlob(composeImage(shot, strokes, note, byline)) }),
      ]);
      toast.success('Copied. Paste it into Gmail, Google Chat or anywhere else.', { toast: true });
    } catch (error) {
      console.error('Copying the screenshot failed:', error);
      toast.error('Could not copy the image. Use Download instead.');
    } finally {
      setBusy('');
    }
  };

  /* Read what the app has recorded. Every press is a fresh read, so
     scanning again after reproducing a problem picks up the new errors.
     The read itself is instant; the overlay sweeps the screen once
     (SCAN_MS) so the press visibly does something, then the report shows. */
  const scanTimer = useRef(null);
  useEffect(() => () => clearTimeout(scanTimer.current), []);
  const scanApp = () => {
    if (busy) return;
    setBusy('scan');
    scanTimer.current = setTimeout(() => {
      setScan(getDevMessages());
      setBusy('');
    }, SCAN_MS);
  };

  // The scan as text, for Google Chat or an email. No note needed: the
  // scan is useful on its own, and the note is added when there is one.
  const copyScan = async () => {
    if (!scan) return;
    const text = formatDevMessages(scan, { by: takenBy, note: note.trim() });
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Scan copied. Paste it into Google Chat or an email.', { toast: true });
    } catch (error) {
      console.error('Copying the scan failed:', error);
      toast.error('Could not copy the scan.');
    }
  };

  const download = async () => {
    if (!hasNote) return;
    setBusy('download');
    try {
      const blob = await canvasToBlob(composeImage(shot, strokes, note, byline));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `screenshot-${stamp()}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Downloading the screenshot failed:', error);
      toast.error('Could not save the image.');
    } finally {
      setBusy('');
    }
  };

  return createPortal(
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pgm-scap-title"
      onClick={() => !busy && onClose()}
    >
      <div className="pg-modal-panel pg-modal-panel--wide scap-panel" onClick={(e) => e.stopPropagation()}>
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="pgm-scap-title">
              Screenshot
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Taken by</dt>
                <dd>{takenBy || '—'}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Marks</dt>
                <dd>{strokes.length}</dd>
              </div>
            </dl>
          </div>
          <button type="button" className="pg-modal-close" onClick={onClose} aria-label="Close" disabled={Boolean(busy)}>
            &times;
          </button>
        </header>

        {/* Two columns: the screenshot on the left at the full height the
            popup has, everything else -- note, markers, scan -- down the
            right. Below 900px they stack, image first. */}
        <div className="pg-modal-body scap-body">
          <div className="scap-stage">
            <canvas
              ref={canvasRef}
              className="scap-canvas"
              width={shot.width}
              height={shot.height}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={finishStroke}
              onPointerCancel={finishStroke}
              aria-label="Screenshot. Drag to draw on it."
            />
          </div>

          <div className="scap-side">
          {/* The note first: it is required, so it must be in view before
              anyone reaches for Copy. */}
          <section className="pgm-section">
            <div className="pg-field">
              <label className="pg-label" htmlFor="scap-note">
                Note <span className="scap-required">Required</span>
              </label>
              <textarea
                id="scap-note"
                className="pg-input pg-textarea scap-note"
                rows={2}
                maxLength={2000}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                required
                aria-required="true"
                autoFocus
                placeholder="What should they look at? It is printed under the image, with your name and email."
              />
            </div>
          </section>

          <section className="pgm-section">
            <div className="scap-tools">
              <div className="scap-swatches" role="group" aria-label="Marker">
                {MARKERS.map((m) => (
                  <button
                    type="button"
                    key={m.key}
                    className={`scap-swatch scap-swatch--${m.key}`}
                    aria-pressed={marker === m.key}
                    onClick={() => setMarker(m.key)}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="pgm-btn"
                onClick={() => setStrokes((prev) => prev.slice(0, -1))}
                disabled={!strokes.length}
              >
                Undo
              </button>
              <button type="button" className="pgm-btn" onClick={() => setStrokes([])} disabled={!strokes.length}>
                Clear
              </button>
            </div>
          </section>

          {/* The scan, once run: failed API calls first -- usually the
              cause -- then the console. Each list scrolls in its own box
              so a long session does not push the screenshot away. */}
          {scan && (
            <section className="pgm-section">
              <h3 className="pgm-section-title">
                App scan
                <span className="pgm-section-count">
                  {scan.apiErrors.length + scan.consoleErrors.length}
                </span>
              </h3>
              <p className="scap-hint">
                Recorded since {new Date(scan.since).toLocaleTimeString()}, when this tab
                loaded the app.
              </p>

              <h4 className="scap-scan-head">
                Failed API calls <span className="pgm-section-count">{scan.apiErrors.length}</span>
              </h4>
              {scan.apiErrors.length === 0 ? (
                <p className="pgm-empty">No API call has failed.</p>
              ) : (
                <div className="pgm-tablescroll scap-scan-list">
                  <table className="pgm-table pgm-table--values">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Call</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scan.apiErrors.map((e, i) => (
                        <tr key={`${e.at}-${i}`}>
                          <td className="pgm-num">{i + 1}</td>
                          <td className="scap-scan-text">
                            {e.method} {e.url}
                            {e.response && <small>{e.response}</small>}
                          </td>
                          <td>{e.status || 'No response'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h4 className="scap-scan-head">
                Console errors <span className="pgm-section-count">{scan.consoleErrors.length}</span>
              </h4>
              {scan.consoleErrors.length === 0 ? (
                <p className="pgm-empty">No console errors.</p>
              ) : (
                <div className="pgm-tablescroll scap-scan-list">
                  <table className="pgm-table pgm-table--values">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Error</th>
                        <th>Page</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scan.consoleErrors.map((e, i) => (
                        <tr key={`${e.at}-${i}`}>
                          <td className="pgm-num">{i + 1}</td>
                          <td className="scap-scan-text">{e.message}</td>
                          <td>{e.page}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          </div>
        </div>

        <footer className="pg-modal-foot">
          <div className="pgm-actions">
            {/* Filled in the theme colour: the one action here worth
                reaching for first when something is broken. */}
            <button type="button" className="pg-btn pg-btn--main" onClick={scanApp} disabled={Boolean(busy)}>
              {busy === 'scan' ? 'Scanning…' : scan ? 'Scan again' : 'Scan App'}
            </button>
            {scan && (
              <button type="button" className="pgm-btn" onClick={copyScan} disabled={Boolean(busy)}>
                Copy scan
              </button>
            )}
            {!hasNote && <span className="scap-hint">Add a note to copy or download</span>}
            <button type="button" className="pgm-btn" onClick={copyImage} disabled={Boolean(busy) || !hasNote}>
              {busy === 'copy' ? 'Copying…' : 'Copy image'}
            </button>
            <button type="button" className="pgm-btn" onClick={download} disabled={Boolean(busy) || !hasNote}>
              Download
            </button>
          </div>
          <button type="button" className="pg-btn pg-btn--main" onClick={onClose} disabled={Boolean(busy)}>
            Close
          </button>
        </footer>
      </div>

      {/* The scan overlay: over the whole app, this popup included, while
          Scan App runs. A line of the theme colour sweeps the screen once.
          Clicks stop here so nothing underneath is pressed mid-scan. */}
      {busy === 'scan' && (
        <div
          className="scap-scanning"
          role="status"
          aria-live="polite"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="scap-scanning-line" aria-hidden="true" />
          <div className="scap-scanning-label">
            <span className="scap-scanning-dot" aria-hidden="true" />
            Scanning the app…
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default ScreenCaptureModal;
